/**
 * Offline catch queue.
 * Catches submitted while offline are queued in localStorage and
 * auto-retried whenever navigator.onLine becomes true. Their photos go in
 * IndexedDB, since a few of them would fill localStorage's quota.
 */

import type {
  BulkCatchBody,
  CreateCatchBody,
  FishType,
  GearType,
  LogFishingDayBody,
} from "@lakseborsen/shared";

const QUEUE_KEY = "lakseborsen:catch_queue";
const BULK_QUEUE_KEY = "lakseborsen:bulk_catch_queue";
const FISHING_DAY_QUEUE_KEY = "lakseborsen:fishing_day_queue";
const DRAFT_KEY = "lakseborsen:catch_draft";
const PREFS_KEY = "lakseborsen:catch_prefs";
const RECENT_FISHER_NAMES_KEY = "lakseborsen:recent_fisher_names";
const MAX_RECENT_FISHER_NAMES = 5;
const PHOTO_DB = "lakseborsen-offline";
const PHOTO_STORE = "queued_photos";

/** A photo captured while offline, held as a data URL until it can be uploaded. */
export interface PendingPhoto {
  dataUrl: string;
  mimeType: string;
}

export interface QueuedCatch {
  id: string;
  body: CreateCatchBody;
  /** Photo held inline in localStorage — only when IndexedDB wasn't usable. */
  pendingPhoto?: PendingPhoto;
  /** The photo is in IndexedDB, under this item's id. */
  hasStoredPhoto?: boolean;
  queuedAt: string;
}

export function saveDraft(body: Partial<CreateCatchBody>) {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(body));
}

export function loadDraft(): Partial<CreateCatchBody> | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

// ── Last-catch preferences (vald + fishType + gear) ──────────────────────────

export interface CatchPrefs {
  valdId?: string;
  fishType?: FishType;
  gear?: GearType;
  fiskerName?: string | null;
  roerName?: string | null;
  registerFor?: "self" | "other";
  released?: boolean;
  fromBoat?: boolean;
}

export function savePrefs(prefs: CatchPrefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function loadPrefs(): CatchPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// ── Recently used "registering for another angler" names ────────────────────
// Lets a guide/family member who registers catches for the same person
// several times in a session pick the name again instead of retyping it.

export function loadRecentFisherNames(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_FISHER_NAMES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addRecentFisherName(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return;
  const existing = loadRecentFisherNames().filter(
    (n) => n.toLowerCase() !== trimmed.toLowerCase(),
  );
  const next = [trimmed, ...existing].slice(0, MAX_RECENT_FISHER_NAMES);
  localStorage.setItem(RECENT_FISHER_NAMES_KEY, JSON.stringify(next));
}

function saveQueue(queue: QueuedCatch[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/**
 * Runs one request against the photo store. The connection is opened per
 * call rather than kept around: iOS can drop it while the app is suspended.
 * Rejects if IndexedDB is unavailable.
 */
async function withPhotoStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(PHOTO_DB, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(PHOTO_STORE);
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(PHOTO_STORE, mode);
      const request = run(tx.objectStore(PHOTO_STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Queues a catch for later submission. If a pending photo is attached but
 * storing it would exceed the browser's storage quota, the catch is queued
 * without the photo rather than losing the whole catch.
 */
export function enqueue(
  body: CreateCatchBody,
  pendingPhoto?: PendingPhoto,
): QueuedCatch {
  const queue = getQueue();
  const item: QueuedCatch = {
    id: crypto.randomUUID(),
    body,
    pendingPhoto,
    queuedAt: new Date().toISOString(),
  };
  queue.push(item);
  try {
    saveQueue(queue);
  } catch {
    // Likely a storage-quota error from the attached photo — drop the
    // photo and retry once before giving up on persisting this item.
    delete item.pendingPhoto;
    try {
      saveQueue(queue);
    } catch {
      // Truly out of storage. The item is still returned so the caller's
      // submit flow can proceed without throwing, but it won't survive a
      // page reload — better than an uncaught exception mid-submit.
    }
  }
  return item;
}

/**
 * Queues a catch with its photo, keeping the photo in IndexedDB. Falls back
 * to enqueue() if IndexedDB can't take it, so the catch is never lost to it.
 */
export async function enqueueWithPhoto(
  body: CreateCatchBody,
  photo: PendingPhoto,
): Promise<QueuedCatch> {
  const id = crypto.randomUUID();
  try {
    await withPhotoStore("readwrite", (store) => store.put(photo, id));
  } catch {
    return enqueue(body, photo);
  }
  const item: QueuedCatch = {
    id,
    body,
    hasStoredPhoto: true,
    queuedAt: new Date().toISOString(),
  };
  try {
    saveQueue([...getQueue(), item]);
  } catch {
    // Truly out of storage — same trade-off as enqueue().
  }
  return item;
}

export function getQueue(): QueuedCatch[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function removeFromQueue(id: string) {
  saveQueue(getQueue().filter((q) => q.id !== id));
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/data:(.*);base64/)?.[1] ?? "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

let queueDraining = false;

/**
 * Drain the offline queue by attempting to POST each item. Items are
 * removed on success; left in queue on failure.
 *
 * If an item has a pending photo (taken while offline, never uploaded),
 * inline or in IndexedDB, the photo is uploaded first via `uploadPhoto` and merged into the body
 * as `imageUrl` before submitting — and that progress is persisted
 * immediately, so a catch-submit failure right after doesn't force the
 * photo to be re-uploaded on the next drain. A stored photo that has since
 * been evicted is gone for good, so the catch is sent without it; a failed
 * read is left to retry, as IndexedDB errors on iOS tend to be transient.
 *
 * Guarded against concurrent invocation: with a single drain trigger
 * mounted app-wide (useOfflineSync) and firing on both mount and every
 * `online` event, two overlapping calls are realistic (React StrictMode's
 * double-invoke in dev, or a genuine `online` event arriving right at
 * mount) — without this guard, both calls would read the same
 * not-yet-removed item and submit it twice, and neither /catches nor
 * /catches/bulk is idempotent server-side.
 */
export async function drainQueue(
  submitFn: (body: CreateCatchBody) => Promise<void>,
  uploadPhoto?: (photo: PendingPhoto) => Promise<string>,
) {
  if (queueDraining) return;
  queueDraining = true;
  try {
    const queue = getQueue();
    for (const item of queue) {
      try {
        const hadStoredPhoto = item.hasStoredPhoto;
        const photo =
          item.pendingPhoto ??
          (hadStoredPhoto
            ? await withPhotoStore<PendingPhoto | undefined>(
                "readonly",
                (store) => store.get(item.id),
              )
            : undefined);
        if (photo && uploadPhoto) {
          const imageUrl = await uploadPhoto(photo);
          item.body = { ...item.body, imageUrl };
          delete item.pendingPhoto;
          delete item.hasStoredPhoto;
          saveQueue(getQueue().map((q) => (q.id === item.id ? item : q)));
          if (hadStoredPhoto) {
            await withPhotoStore("readwrite", (store) =>
              store.delete(item.id),
            ).catch(() => {});
          }
        }
        await submitFn(item.body);
        removeFromQueue(item.id);
      } catch {
        // leave in queue, will retry next time
      }
    }
  } finally {
    queueDraining = false;
  }
}

/** Converts a queued photo back into a Blob, ready to PUT to a presigned URL. */
export function pendingPhotoToBlob(photo: PendingPhoto): Blob {
  return dataUrlToBlob(photo.dataUrl);
}

// ── Bulk (småfisk) offline queue ─────────────────────────────────────────────
// Mirrors the single-catch queue above for POST /catches/bulk, which has a
// different body shape (count/avg weight instead of one fish's weight).

export interface QueuedBulkCatch {
  id: string;
  body: BulkCatchBody;
  queuedAt: string;
}

export function enqueueBulk(body: BulkCatchBody): QueuedBulkCatch {
  const queue = getBulkQueue();
  const item: QueuedBulkCatch = {
    id: crypto.randomUUID(),
    body,
    queuedAt: new Date().toISOString(),
  };
  queue.push(item);
  try {
    localStorage.setItem(BULK_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Out of storage — nothing to drop from a bulk body to help; same
    // trade-off as enqueue() above.
  }
  return item;
}

export function getBulkQueue(): QueuedBulkCatch[] {
  try {
    const raw = localStorage.getItem(BULK_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function removeFromBulkQueue(id: string) {
  localStorage.setItem(
    BULK_QUEUE_KEY,
    JSON.stringify(getBulkQueue().filter((q) => q.id !== id)),
  );
}

let bulkQueueDraining = false;

/** Guarded the same way as drainQueue() above — see its comment for why. */
export async function drainBulkQueue(
  submitFn: (body: BulkCatchBody) => Promise<void>,
) {
  if (bulkQueueDraining) return;
  bulkQueueDraining = true;
  try {
    const queue = getBulkQueue();
    for (const item of queue) {
      try {
        await submitFn(item.body);
        removeFromBulkQueue(item.id);
      } catch {
        // leave in queue, will retry next time
      }
    }
  } finally {
    bulkQueueDraining = false;
  }
}

// ── Fishing day (zero-catch check-in) offline queue ──────────────────────────
// Mirrors the catch queues above for POST /fishing-sessions, which — unlike
// catches — used to fail silently when offline: a guide logging several
// zero-catch days in a dead zone would tap "log fishing day" and see no
// feedback at all once the request couldn't reach the server.

export interface QueuedFishingDay {
  id: string;
  body: LogFishingDayBody;
  queuedAt: string;
}

export function enqueueFishingDay(body: LogFishingDayBody): QueuedFishingDay {
  const queue = getFishingDayQueue();
  const item: QueuedFishingDay = {
    id: crypto.randomUUID(),
    body,
    queuedAt: new Date().toISOString(),
  };
  queue.push(item);
  try {
    localStorage.setItem(FISHING_DAY_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Out of storage — nothing to drop to help; same trade-off as enqueue().
  }
  return item;
}

export function getFishingDayQueue(): QueuedFishingDay[] {
  try {
    const raw = localStorage.getItem(FISHING_DAY_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function removeFromFishingDayQueue(id: string) {
  localStorage.setItem(
    FISHING_DAY_QUEUE_KEY,
    JSON.stringify(getFishingDayQueue().filter((q) => q.id !== id)),
  );
}

let fishingDayQueueDraining = false;

/** Guarded the same way as drainQueue() above — see its comment for why. */
export async function drainFishingDayQueue(
  submitFn: (body: LogFishingDayBody) => Promise<void>,
) {
  if (fishingDayQueueDraining) return;
  fishingDayQueueDraining = true;
  try {
    const queue = getFishingDayQueue();
    for (const item of queue) {
      try {
        await submitFn(item.body);
        removeFromFishingDayQueue(item.id);
      } catch {
        // leave in queue, will retry next time
      }
    }
  } finally {
    fishingDayQueueDraining = false;
  }
}
